import { InsertFilesSubmitParams } from 'components/modals/InsertFilesModal/InsertFilesModal';
import Immutable, { List } from 'immutable';
import { RawInsertedPunchPhoto } from 'records/InsertedPunchPhoto';
import { NewPunch } from 'records/NewPunch';
import { ProjectDriveSelectedItem } from 'records/ProjectDriveSelectedItem';
import { Punch, PunchList, RawPunch } from 'records/Punch';
import { UploadData } from 'records/UploadData';
import { beginUpload, completeUpload } from 'services/fileService';
import GQLRequest from 'utils/GQLRequest';
import { v4 as uuidv4 } from 'uuid';
import { RFIExportItem } from './rfiService';

interface RawPunchResponse {
  issues: RawPunch[];
  cursor: string;
}

interface PunchResponse {
  issues: PunchList;
  cursor: string;
}

interface UploadResponse {
  fileid: string;
  signedurl: string;
}

interface PhotoUploadFile {
  photoid: string;
  projectid: string;
  issueid: string;
}

interface AddInsertedPhotosResponse {
  success: boolean;
  photos: RawInsertedPunchPhoto[];
}

const punchSchema = `
id
projectid
description
issuetitle
dbcreateddate
completeddate
author {
  id
  email
}
clientcreateddate
location
status
assignees
groupAssignees
duedate
disciplines
comment
priority
number
photoids
markupids
issueStatus {
  status
  displayString
  statusColor {
    space
    components
  }
}
`;

export const getPunch = async (
  projectid: string,
  issueid: string
): Promise<Punch> => {
  const { issue } = await new GQLRequest()
    .query(
      `
    query Punch($issueid: String!, $projectid: String!) {
      issue(issueid: $issueid, projectid: $projectid) {
        ${punchSchema}
      }
    }
    `
    )
    .variables({ projectid, issueid })
    .json<{ issue: RawPunch }>();
  return Punch.deserialize(issue);
};

export const getNextPunches = async (
  projectid: string,
  cursor: string
): Promise<PunchResponse> => {
  const { getNextIssues } = await new GQLRequest()
    .query(
      `
    query GetNextIssues($projectid: String!, $cursor: String!) {
      getNextIssues(projectid: $projectid, cursor: $cursor) {
        issues {
          ${punchSchema}
        }
        cursor
      }
    }
    `
    )
    .variables({ projectid, cursor })
    .json<{ getNextIssues: RawPunchResponse }>();

  return {
    issues: Immutable.List(getNextIssues.issues.map(Punch.deserialize)),
    cursor: getNextIssues.cursor,
  };
};

export const getPunches = async (
  projectid: string,
  pageSize = 500
): Promise<PunchList> => {
  const { getIssues } = await new GQLRequest()
    .query(
      `
    query GetIssues($projectid: String!, $pageSize: Int!) {
      getIssues(projectid: $projectid, pageSize: $pageSize) {
        issues {
          ${punchSchema}
        }
        cursor
      }
    }
    `
    )
    .variables({ projectid, pageSize })
    .json<{ getIssues: RawPunchResponse }>();

  let punchList = Immutable.List(getIssues.issues.map(Punch.deserialize));
  let { cursor } = getIssues;

  while (cursor) {
    const { issues: nextIssues, cursor: nextCursor } = await getNextPunches(
      projectid,
      cursor
    );

    punchList = punchList.concat(nextIssues);
    cursor = nextCursor;
  }

  return punchList;
};

export const createPunch = async (newPunch: NewPunch): Promise<Punch> => {
  try {
    const punchData = newPunch.serialize();

    const punchId = uuidv4();
    const {
      createIssue: { issue, uploadResponses },
    } = await new GQLRequest()
      .query(
        `
        mutation CreatePunch($newPunch: NewIssue!, $punchId: String) {
          createIssue(intput: $newPunch, clientGeneratedID: $punchId) {
            issue {
              ${punchSchema}
            }
            uploadResponses {
              fileid
              signedurl
            }
          }
        }
        `
      )
      .variables({ newPunch: punchData, punchId })
      .json<{
        createIssue: {
          issue: RawPunch;
          uploadResponses: UploadResponse[];
        };
      }>();

    await Promise.all(
      uploadResponses
        .map((v: UploadResponse, k): [UploadData, UploadResponse] => [
          newPunch.getIn(['photos', k]),
          v,
        ])
        .map(
          async ([uploadData, { fileid, signedurl }]: [
            UploadData,
            UploadResponse
          ]): Promise<void> => {
            const resp = await fetch(signedurl, {
              method: 'PUT',
              body: uploadData.file,
            });

            if (resp.ok) {
              await completeUpload(uploadData.projectid, fileid);
            }
          }
        )
    );

    return Punch.deserialize(issue);
  } catch (err) {
    console.log(err);
    throw err;
  }
};

export async function completePhotoUpload(
  photoUploadFile: PhotoUploadFile
): Promise<Punch> {
  const resp = await new GQLRequest()
    .query(
      `
      mutation CompletePhotoUpload($input: PhotoUploadFile) {
        completePhotoUpload(input: $input) {
          ${punchSchema}
        }
      }
      `
    )
    .variables({ input: photoUploadFile })
    .json<{ completePhotoUpload: RawPunch }>();

  return Punch.deserialize(resp.completePhotoUpload);
}

export async function addPhotosToPunch(
  punch: Punch,
  uploadedPhotos: File[],
  insertedPhotoIds: string[]
): Promise<Punch> {
  const uploadedFileIds = await Promise.all(
    uploadedPhotos.map(async (file) => {
      const { fileid, signedurl } = await beginUpload(
        new UploadData({
          projectid: punch.projectid,
          filename: file.name,
          category: 'imageattachment',
          extension: file.type,
          filesize: file.size,
          file,
        })
      );

      const uploadResp = await fetch(signedurl, {
        method: 'PUT',
        body: file,
      });

      if (!uploadResp.ok) {
        throw new Error('addPhotoToPunch: unable to upload photo');
      }

      return fileid;
    })
  );

  const allFileIds = uploadedFileIds.concat(insertedPhotoIds);

  let updatedPunch = new Punch();
  for (const fileid of allFileIds) {
    updatedPunch = await completePhotoUpload({
      photoid: fileid,
      projectid: punch.projectid,
      issueid: punch.id,
    });
  }

  return updatedPunch;
}

export const updatePunch = async (
  punch: Punch,
  uploadedPhotos: File[] = [],
  insertedPhotoIds: string[] = []
): Promise<Punch> => {
  const resp = await new GQLRequest()
    .query(
      `
      mutation UpdatePunch($updatedPunch: UpdateIssue!) {
        updateIssue(issue: $updatedPunch) {
          ${punchSchema}
        }
      }
      `
    )
    .variables({ updatedPunch: punch.toRawUpdatePunch() })
    .json<{ updateIssue: RawPunch }>();

  if (uploadedPhotos.length > 0 || insertedPhotoIds.length > 0) {
    const updatedPunch = await addPhotosToPunch(
      punch,
      uploadedPhotos,
      insertedPhotoIds
    );

    return updatedPunch;
  }

  return Punch.deserialize(resp.updateIssue);
};

export const deletePunch = async (punch: Punch): Promise<string> => {
  const { deleteIssue: deleteIssueId } = await new GQLRequest()
    .query(
      `
      mutation DeletePunch($projectid: String!, $issueid: String!) {
        deleteIssue(projectid: $projectid, issueid: $issueid)
      }
      `
    )
    .variables({ projectid: punch.projectid, issueid: punch.id })
    .json<{ deleteIssue: string }>();

  return deleteIssueId;
};

export const getPunchSnapshotUrl = async (
  projectid: string,
  markupid: string,
  width: number,
  height: number
): Promise<string> => {
  const { issueMarkupSnapshot } = await new GQLRequest()
    .query(
      `query IssueMarkupSnapshot($projectid: String!, $markupid: String!, $width: Int!, $height: Int!) {
        issueMarkupSnapshot(projectid: $projectid, markupid: $markupid, width: $width, height: $height, scale: 1) {
          url
          extension
        }
      }
      `
    )
    .variables({ projectid, markupid, width, height })
    .json<{ issueMarkupSnapshot: RFIExportItem }>();

  return issueMarkupSnapshot.url;
};

const driveItemsToAddInsertPhotosInput = (
  projectId: string,
  items: List<ProjectDriveSelectedItem>
): {
  projectid: string;
  imports: {
    driveid: string;
    fileid: string;
  }[];
} => {
  const imports = items
    .map((item) => ({
      driveid: item.driveId,
      fileid: item.id,
    }))
    .toArray();

  return {
    projectid: projectId,
    imports,
  };
};

// TODO: can handle studio files as well
export const addInsertPhotos = async (
  projectId: string,
  selection: InsertFilesSubmitParams
): Promise<AddInsertedPhotosResponse> => {
  const input = driveItemsToAddInsertPhotosInput(
    projectId,
    selection.projectDriveSelectedItems
  );

  const { addIssuePhotoFromDms } = await new GQLRequest()
    .query(
      `
    mutation AddInsertedPhotos($input: AddIssuePhotoFromDmsRequest) {
      addIssuePhotoFromDms(input: $input) {
        success
        photos {
          fileid
          name
          url
          extension
          size              
        }
      }
    }
  `
    )
    .variables({ input })
    .json<{
      addIssuePhotoFromDms: AddInsertedPhotosResponse;
    }>();
  return addIssuePhotoFromDms;
};

export const deleteInsertedPhoto = async (
  projectid: string,
  insertedPhotoId: string
): Promise<string> => {
  const { removePhoto: photoId } = await new GQLRequest()
    .query(
      `
      mutation RemovePhoto($projectid: String!, $issueid: String, $photoid: String!) {
        removePhoto(projectid: $projectid, issueid: $issueid, photoid: $photoid)
      }
      `
    )
    .variables({ projectid, issueid: null, photoid: insertedPhotoId })
    .json<{ removePhoto: string }>();

  return photoId;
};

export const deletePunchPhoto = async (
  projectid: string,
  insertedPhotoId: string,
  issueId: string
): Promise<string> => {
  const { removePhoto: photoId } = await new GQLRequest()
    .query(
      `
      mutation RemovePhoto($projectid: String!, $issueid: String, $photoid: String!) {
        removePhoto(projectid: $projectid, issueid: $issueid, photoid: $photoid)
      }
      `
    )
    .variables({ projectid, issueid: issueId, photoid: insertedPhotoId })
    .json<{ removePhoto: string }>();

  return photoId;
};

export const deletePunchPhotos = async (
  projectId: string,
  issueId: string,
  insertedPhotoIds: string[]
): Promise<string[]> => {
  const deleteRequests = insertedPhotoIds.map((id) =>
    deletePunchPhoto(projectId, id, issueId)
  );
  const deleteResponses = await Promise.allSettled(deleteRequests);

  const successfullyDeleted = deleteResponses
    .reduce<PromiseFulfilledResult<string>[]>((fulfilled, current) => {
      if (current.status === 'fulfilled') fulfilled.push(current);

      return fulfilled;
    }, [])
    .map((result) => result.value);

  return successfullyDeleted;
};

// TODO: Possible optimization: merge into a single delete
// method which always sends out a [] of inserted photo ids
export const deleteInsertedPhotos = async (
  projectId: string,
  insertedPhotoIds: string[]
): Promise<string[]> => {
  const deleteRequests = insertedPhotoIds.map((id) =>
    deleteInsertedPhoto(projectId, id)
  );
  const deleteResponses = await Promise.allSettled(deleteRequests);

  const successfullyDeleted = deleteResponses
    .reduce<PromiseFulfilledResult<string>[]>((fulfilled, current) => {
      if (current.status === 'fulfilled') fulfilled.push(current);

      return fulfilled;
    }, [])
    .map((result) => result.value);

  return successfullyDeleted;
};

export default {
  getPunches,
  createPunch,
  updatePunch,
  deletePunch,
  getPunchSnapshotUrl,
  completePhotoUpload,
  addInsertPhotos,
  deleteInsertedPhoto,
  deletePunchPhotos,
};
